import { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { IdentityUserAri } from '@atlassian/ari/identity';
import { type EntityATI, type ThirdPartyFilters } from '@atlassian/search-client';

import { type TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { ProductKeys } from '../../../common/constants/products';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import {
	SearchExperience,
	type SearchStateFilters,
	type SearchStoreExperience,
} from '../../../common/constants/search';
import { type SelectedFiltersType } from '../../../common/constants/types';
import { getThirdPartyFilters } from '../../../common/utils/filters';
import { getProducts } from '../../../common/utils/search';
import { useBootstrap, useCurrentUserId } from '../../store/bootstrap';
import {
	getTypeEntities,
	useFilters,
	useJsmIncidentFilterIds,
	useProducts,
	useSearch,
	useSelectedDatasource,
	useTypeEntities,
} from '../../store/search';
import {
	archivedAndPendingComponentsFilter,
	archivedFilter,
	assetsSchemaFilter,
	assetsTypeFilter,
	assigneeFilter,
	commentedByMeFilter,
	contentFilter,
	contributorFilter,
	createdByFilter,
	focusAreaTypeFilter,
	focusAreaUnderFilter,
	labelFilter,
	lastUpdatedByFilter,
	lastUpdatedFilter,
	ownerFilter,
	pageStatusFilter,
	positionEmploymentTypeFilter,
	positionFocusAreaFilter,
	positionJobFamilyFilter,
	positionStatusFilter,
	positionTeamFilter,
	projectFilter,
	reporterFilter,
	spaceFilter,
	statusFilter,
	titleMatchFilter,
	verifiedFilter,
	watchedFilter,
} from '../../store/search/filters';

import {
	getArchivedAndPendingComponentsValue,
	getBooleanFilterValue,
	getContainerStatusValue,
	getContentStatusValue,
	getFilterDateRange,
	getJiraIssueStatusFilterOptions,
	getJiraProjectFilter,
	getSelectedDateRange,
	getSelectedJiraWorkType,
	getSelectFilterValue,
	omitNonJiraFilterValues,
	useJiraIssueTypeFilters,
} from './utils';

export type SelectedFiltersReturnType = {
	selectedFilters: SelectedFiltersType;
	unselectedProductsThirdPartyFilters: ThirdPartyFilters;
	hasFiltersSelected: boolean;
	selectedEntities: string[];
	entitiesForResultsCountQuery: EntityATI[];
	entities: EntityATI[];
};

type GetSelectedFiltersPropsFromBootstrap = {
	availableProducts: ProductKeys[];
	allFilterExcludedProducts: ProductKeys[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	currentUserId?: string | null;
};

type GetSelectedFiltersPropsFromSearch = {
	filters: SearchStateFilters;
	selectedProducts: ProductKeys[];
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	jsmIncidentFilterIds: string[];
	experience?: SearchStoreExperience;
};

type GetSelectedFiltersProps = GetSelectedFiltersPropsFromBootstrap &
	GetSelectedFiltersPropsFromSearch;

export const getSelectedFilters = ({
	filters,
	selectedProducts,
	selectedDatasource,
	availableProducts: products,
	allFilterExcludedProducts,
	thirdPartyConfigs,
	currentUserId,
	jsmIncidentFilterIds,
	experience = SearchExperience.SEARCH_PAGE,
}: GetSelectedFiltersProps): SelectedFiltersReturnType => {
	const { selectedProduct, filteredProducts, availableProducts } = getProducts({
		selectedProducts,
		availableProducts: products,
	});
	const { selectedEntities, entitiesForQuery, entitiesForResultsCountQuery, selectedTypes } =
		getTypeEntities(
			{ availableProducts, filters, selectedProducts, allFilterExcludedProducts },
			{
				thirdPartyConfigs,
			},
		);

	const watchedByMeValue = getBooleanFilterValue(filters[watchedFilter.id]);
	const titleMatchValue = getBooleanFilterValue(filters[titleMatchFilter.id]);
	const selectedDateRange = getSelectedDateRange(filters[lastUpdatedFilter.id]);
	const filterDateRange = getFilterDateRange(filters[lastUpdatedFilter.id]);
	const commonContributors = getSelectFilterValue(filters[contributorFilter.id]).length
		? getSelectFilterValue(filters[contributorFilter.id])
		: undefined;
	const pageStatusValue = getSelectFilterValue(filters[pageStatusFilter.id])[0];

	const assigneeARIValues = getSelectFilterValue(filters[assigneeFilter.id]).map((assigneeId) => {
		return `ari:cloud:identity::user/${assigneeId}`;
	});
	const reporterARIValues = fg('rovo_search_in_jira_t25_fps_filters')
		? getSelectFilterValue(filters[reporterFilter.id]).map((reporterId) => {
				return `ari:cloud:identity::user/${reporterId}`;
			})
		: [];

	const watcherARIValues = (watchedByMeValue && currentUserId ? [currentUserId] : []).map(
		(watcherId) => {
			return `ari:cloud:identity::user/${watcherId}`;
		},
	);

	const commenterARIValues =
		getBooleanFilterValue(filters[commentedByMeFilter.id]) &&
		currentUserId &&
		fg('rovo_search_in_jira_commenter_filter')
			? [`ari:cloud:identity::user/${currentUserId}`]
			: [];

	const assignee3PValues = getSelectFilterValue(filters[assigneeFilter.id]).map((assigneeId) => {
		if (assigneeId.startsWith('ari:cloud:')) {
			return IdentityUserAri.parse(assigneeId).resourceId;
		}
		return assigneeId;
	});

	let containerAris = [
		...getSelectFilterValue(filters[projectFilter.id]),
		...getSelectFilterValue(filters[spaceFilter.id]),
	];

	const createdByValues = getSelectFilterValue(filters[createdByFilter.id]);
	const labelsValues = getSelectFilterValue(filters[labelFilter.id]);
	const lastUpdatedByValues = getSelectFilterValue(filters[lastUpdatedByFilter.id]);
	const ancestorAris = getSelectFilterValue(filters[contentFilter.id]);

	const selectedFilters = {
		commonFilters: {
			range: {
				lastModified: selectedDateRange,
			},
			contributorsFilter: commonContributors,
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: omitNonJiraFilterValues(
					getSelectFilterValue(filters[projectFilter.id]),
					selectedProduct,
				),
				watcherARIs: omitNonJiraFilterValues(watcherARIValues, selectedProduct),
				assigneeARIs: omitNonJiraFilterValues(assigneeARIValues, selectedProduct),
				issueTypeIDs: fg('search-page-relay-useselectedfilters-step-1') ? jsmIncidentFilterIds : [], // fg=false branch not implemented in relay due to fetch request and dependency on intl
				...(fg('rovo_search_in_jira_t25_fps_filters')
					? {
							issueLabels: omitNonJiraFilterValues(
								getSelectFilterValue(filters[labelFilter.id]),
								selectedProduct,
							),
							statusCategories: omitNonJiraFilterValues(
								getJiraIssueStatusFilterOptions(filters[statusFilter.id]),
								selectedProduct,
							),
							reporterARIs: omitNonJiraFilterValues(reporterARIValues, selectedProduct),
							commenterARIs: omitNonJiraFilterValues(commenterARIValues, selectedProduct),
						}
					: {}),
				...(fg('rovo_jira_work_type')
					? { issueTypes: getSelectedJiraWorkType(selectedTypes, selectedProduct) }
					: {}),
			},
			projectFilter: {
				projectTypes:
					experience === SearchExperience.QUICK_FIND ? [] : getJiraProjectFilter(selectedProduct),
			},
		},
		assetsFilters: {
			objectSchemaFilter: {
				schemaARIs: fg('assets_rovo_search_all')
					? getSelectFilterValue(filters[assetsSchemaFilter.id])
					: [],
			},
			objectTypeFilter: {
				typeARIs: fg('assets_rovo_search_all')
					? getSelectFilterValue(filters[assetsTypeFilter.id])
					: [],
			},
		},
		confluenceFilters: {
			spacesFilter: getSelectFilterValue(filters[spaceFilter.id]),
			isVerified: getBooleanFilterValue(filters[verifiedFilter.id]) || undefined,
			contributorsFilter: getSelectFilterValue(filters[contributorFilter.id]),
			ancestorIdsFilter: getSelectFilterValue(filters[contentFilter.id]),
			range: selectedProduct !== ProductKeys.Confluence ? filterDateRange : [],
			owners: getSelectFilterValue(filters[ownerFilter.id]),
			labelsFilter:
				selectedProduct === ProductKeys.Confluence
					? getSelectFilterValue(filters[labelFilter.id])
					: [],
			titleMatchOnly: titleMatchValue,
			...(pageStatusValue ? { pageStatus: [pageStatusValue] } : {}),
			contentStatuses:
				selectedProduct === ProductKeys.Confluence
					? getContentStatusValue(filters[archivedFilter.id])
					: [],
			containerStatus:
				selectedProduct === ProductKeys.Confluence
					? getContainerStatusValue(filters[archivedFilter.id])
					: [],
		},
		mercuryFilters: {
			owners: getSelectFilterValue(filters[ownerFilter.id]),
			focusAreaTypeIds: getSelectFilterValue(filters[focusAreaTypeFilter.id]),
			ancestorIds: getSelectFilterValue(filters[focusAreaUnderFilter.id]),
		},
		...(fg('talent_global_search')
			? {
					talentFilters: {
						owners: getSelectFilterValue(filters[ownerFilter.id]),
						employmentTypes: getSelectFilterValue(filters[positionEmploymentTypeFilter.id]).map(
							(value) => decodeURIComponent(value),
						),
						focusAreas: getSelectFilterValue(filters[positionFocusAreaFilter.id]),
						jobFamilies: getSelectFilterValue(filters[positionJobFamilyFilter.id]).map((value) =>
							decodeURIComponent(value),
						),
						statuses: getSelectFilterValue(filters[positionStatusFilter.id]).map((value) =>
							decodeURIComponent(value),
						),
						teams: getSelectFilterValue(filters[positionTeamFilter.id]),
					},
				}
			: {}),
		compassFilters: {
			componentFilter: {
				componentStates: getArchivedAndPendingComponentsValue(
					filters[archivedAndPendingComponentsFilter.id],
				),
			},
		},
		thirdPartyFilters: getThirdPartyFilters(
			filteredProducts,
			titleMatchValue,
			selectedEntities as TypeFilterValueKey[],
			thirdPartyConfigs,
			filterDateRange,
			assignee3PValues,
			containerAris,
			createdByValues,
			lastUpdatedByValues,
			ancestorAris,
			labelsValues,
			undefined,
			selectedDatasource?.datasourceId,
		),
	} satisfies SelectedFiltersType;

	const unselectedProductsThirdPartyFilters = getThirdPartyFilters(
		availableProducts,
		titleMatchValue,
		selectedEntities as TypeFilterValueKey[],
		thirdPartyConfigs,
		filterDateRange,
		undefined, // assignees
		undefined, // containerAris
		undefined, // createdBy
		undefined, // lastUpdatedBy
		undefined, // ancestorAris
		undefined, // labels
		undefined, // additionalTexts
		undefined, // selecteddatasourceid
	);

	const {
		commonFilters,
		jiraFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		talentFilters,
	} = selectedFilters;

	const isEmpty =
		!commonFilters.range.lastModified &&
		!commonFilters.contributorsFilter &&
		selectedEntities.length === 0 &&
		confluenceFilters.spacesFilter.length === 0 &&
		!confluenceFilters.isVerified &&
		!confluenceFilters.titleMatchOnly &&
		confluenceFilters.contentStatuses.length === 0 &&
		confluenceFilters.containerStatus.length === 0 &&
		confluenceFilters.contributorsFilter.length === 0 &&
		confluenceFilters.ancestorIdsFilter.length === 0 &&
		confluenceFilters.range.length === 0 &&
		confluenceFilters.owners.length === 0 &&
		confluenceFilters.labelsFilter.length === 0 &&
		(!confluenceFilters.pageStatus || confluenceFilters.pageStatus.length === 0) &&
		jiraFilters.issueFilter.assigneeARIs.length === 0 &&
		jiraFilters.issueFilter.projectARIs.length === 0 &&
		(!jiraFilters.issueFilter.statusCategories ||
			jiraFilters.issueFilter.statusCategories?.length === 0) &&
		(!jiraFilters.issueFilter['reporterARIs'] ||
			jiraFilters.issueFilter.reporterARIs.length === 0) &&
		(!jiraFilters.issueFilter['commenterARIs'] ||
			jiraFilters.issueFilter.commenterARIs.length === 0) &&
		mercuryFilters.owners.length === 0 &&
		// When cleaning up rovo_search_in_jira_t25_fps_filters change this to jiraFilters.issueFilter.issueLabels.length === 0
		(!jiraFilters.issueFilter.issueLabels || jiraFilters.issueFilter.issueLabels?.length === 0) &&
		// When cleaning up rovo_jira_work_type change this to jiraFilters.issueFilter.issueTypes.length === 0
		(!jiraFilters.issueFilter.issueTypes || jiraFilters.issueFilter.issueTypes?.length === 0) &&
		mercuryFilters.owners.length === 0 &&
		mercuryFilters.focusAreaTypeIds.length === 0 &&
		mercuryFilters.ancestorIds.length === 0 &&
		compassFilters.componentFilter.componentStates.length === 0 &&
		(fg('talent_global_search')
			? talentFilters?.owners.length === 0 &&
				talentFilters?.employmentTypes.length === 0 &&
				talentFilters?.focusAreas.length === 0 &&
				talentFilters?.jobFamilies.length === 0 &&
				talentFilters?.statuses.length === 0 &&
				talentFilters?.teams.length === 0
			: true) &&
		(fg('assets_rovo_search_all')
			? assetsFilters.objectSchemaFilter.schemaARIs.length === 0 &&
				assetsFilters.objectTypeFilter.typeARIs.length === 0
			: true) &&
		(!thirdPartyFilters.range || Object.keys(thirdPartyFilters.range).length === 0) &&
		!thirdPartyFilters.titleMatchOnly &&
		thirdPartyFilters.subtypes.length === 0 &&
		(!thirdPartyFilters.assignees || thirdPartyFilters.assignees?.length === 0) &&
		(!thirdPartyFilters.lastUpdatedBy || thirdPartyFilters.lastUpdatedBy?.length === 0) &&
		(!thirdPartyFilters.createdBy || thirdPartyFilters.createdBy?.length === 0) &&
		(!thirdPartyFilters.ancestorAris || thirdPartyFilters.ancestorAris?.length === 0);
	return {
		selectedFilters,
		unselectedProductsThirdPartyFilters,
		selectedEntities,
		entitiesForResultsCountQuery,
		entities: entitiesForQuery,
		hasFiltersSelected: !isEmpty,
	};
};

const useWrappedSelectedFilters = ({
	cloudId,
	thirdPartyConfigs,
	experience = SearchExperience.SEARCH_PAGE,
}: {
	cloudId: string | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	experience?: SearchStoreExperience;
}): SelectedFiltersReturnType => {
	const filters = useFilters();
	const [selectedDatasource] = useSelectedDatasource();
	const { selectedProduct, filteredProducts, availableProducts } = useProducts();
	const currentUserId = useCurrentUserId();

	const useTypeEntitiesArgs = useMemo(
		() => ({
			thirdPartyConfigs,
		}),
		[thirdPartyConfigs],
	);

	const { selectedEntities, entitiesForQuery, entitiesForResultsCountQuery, selectedTypes } =
		useTypeEntities(useTypeEntitiesArgs);

	// This call can be removed when cleaning up search-page-relay-useselectedfilters-step-1
	const jiraIssueTypeFilters = useJiraIssueTypeFilters(cloudId, selectedEntities); // contains a fetch call
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const jsmIncidentFilterIds = useJsmIncidentFilterIds(); // just a getter from state

	const watchedByMeValue = useMemo(
		() => getBooleanFilterValue(filters[watchedFilter.id]),
		[filters],
	);

	const titleMatchValue = useMemo(
		() => getBooleanFilterValue(filters[titleMatchFilter.id]),
		[filters],
	);

	const selectedDateRange = useMemo(
		() => getSelectedDateRange(filters[lastUpdatedFilter.id]),
		[filters],
	);

	const filterDateRange = useMemo(
		() => getFilterDateRange(filters[lastUpdatedFilter.id]),
		[filters],
	);

	const commonContributors = useMemo(() => {
		const contributors = getSelectFilterValue(filters[contributorFilter.id]);
		return contributors.length ? contributors : undefined;
	}, [filters]);

	const pageStatusValue = useMemo(
		() => getSelectFilterValue(filters[pageStatusFilter.id])[0],
		[filters],
	);

	const watcherARIValues = useMemo(
		() =>
			(watchedByMeValue && currentUserId ? [currentUserId] : []).map((watcherId) => {
				return `ari:cloud:identity::user/${watcherId}`;
			}),
		[watchedByMeValue, currentUserId],
	);

	const assigneeARIValues = useMemo(
		() =>
			getSelectFilterValue(filters[assigneeFilter.id]).map((assigneeId) => {
				return `ari:cloud:identity::user/${assigneeId}`;
			}),
		[filters],
	);

	const assignee3PValues = useMemo(
		() => getSelectFilterValue(filters[assigneeFilter.id]),
		[filters],
	);

	const reporterARIValues = useMemo(
		() =>
			fg('rovo_search_in_jira_t25_fps_filters')
				? getSelectFilterValue(filters[reporterFilter.id]).map((reporterId) => {
						return `ari:cloud:identity::user/${reporterId}`;
					})
				: [],
		[filters],
	);

	const commenterARIValues = useMemo(
		() =>
			getBooleanFilterValue(filters[commentedByMeFilter.id]) &&
			currentUserId &&
			fg('rovo_search_in_jira_commenter_filter')
				? [`ari:cloud:identity::user/${currentUserId}`]
				: [],
		[filters, currentUserId],
	);

	const containerAris = useMemo(
		() => [
			...getSelectFilterValue(filters[projectFilter.id]),
			...getSelectFilterValue(filters[spaceFilter.id]),
		],
		[filters],
	);

	const ancestorAris = useMemo(() => getSelectFilterValue(filters[contentFilter.id]), [filters]);

	const createdByValues = useMemo(
		() => getSelectFilterValue(filters[createdByFilter.id]),
		[filters],
	);

	const labelsValues = useMemo(() => getSelectFilterValue(filters[labelFilter.id]), [filters]);

	const lastUpdatedByValues = useMemo(
		() => getSelectFilterValue(filters[lastUpdatedByFilter.id]),
		[filters],
	);

	const selectedFilters = useMemo(
		() =>
			({
				commonFilters: {
					range: {
						lastModified: selectedDateRange,
					},
					contributorsFilter: commonContributors,
				},
				jiraFilters: {
					issueFilter: {
						projectARIs: omitNonJiraFilterValues(
							getSelectFilterValue(filters[projectFilter.id]),
							selectedProduct,
						),
						watcherARIs: omitNonJiraFilterValues(watcherARIValues, selectedProduct),
						assigneeARIs: omitNonJiraFilterValues(assigneeARIValues, selectedProduct),
						issueTypeIDs: fg('search-page-relay-useselectedfilters-step-1')
							? jsmIncidentFilterIds
							: jiraIssueTypeFilters,
						...(fg('rovo_search_in_jira_t25_fps_filters')
							? {
									issueLabels: omitNonJiraFilterValues(
										getSelectFilterValue(filters[labelFilter.id]),
										selectedProduct,
									),
									statusCategories: omitNonJiraFilterValues(
										getJiraIssueStatusFilterOptions(filters[statusFilter.id]),
										selectedProduct,
									),
									reporterARIs: omitNonJiraFilterValues(reporterARIValues, selectedProduct),
									commenterARIs: omitNonJiraFilterValues(commenterARIValues, selectedProduct),
								}
							: {}),
						...(fg('rovo_jira_work_type')
							? { issueTypes: getSelectedJiraWorkType(selectedTypes, selectedProduct) }
							: {}),
					},
					projectFilter: {
						projectTypes:
							experience === SearchExperience.QUICK_FIND
								? []
								: getJiraProjectFilter(selectedProduct),
					},
				},
				assetsFilters: {
					objectSchemaFilter: {
						schemaARIs: fg('assets_rovo_search_all')
							? getSelectFilterValue(filters[assetsSchemaFilter.id])
							: [],
					},
					objectTypeFilter: {
						typeARIs: fg('assets_rovo_search_all')
							? getSelectFilterValue(filters[assetsTypeFilter.id])
							: [],
					},
				},
				confluenceFilters: {
					spacesFilter:
						selectedProduct === ProductKeys.Confluence
							? getSelectFilterValue(filters[spaceFilter.id])
							: [],
					isVerified: getBooleanFilterValue(filters[verifiedFilter.id]) || undefined,
					contributorsFilter: getSelectFilterValue(filters[contributorFilter.id]),
					ancestorIdsFilter:
						selectedProduct === ProductKeys.Confluence
							? getSelectFilterValue(filters[contentFilter.id])
							: [],
					range: selectedProduct !== ProductKeys.Confluence ? filterDateRange : [],
					owners: getSelectFilterValue(filters[ownerFilter.id]),
					labelsFilter:
						selectedProduct === ProductKeys.Confluence
							? getSelectFilterValue(filters[labelFilter.id])
							: [],
					titleMatchOnly: titleMatchValue,
					...(pageStatusValue ? { pageStatus: [pageStatusValue] } : {}),
					contentStatuses:
						selectedProduct === ProductKeys.Confluence
							? getContentStatusValue(filters[archivedFilter.id])
							: [],
					containerStatus:
						selectedProduct === ProductKeys.Confluence
							? getContainerStatusValue(filters[archivedFilter.id])
							: [],
				},
				mercuryFilters: {
					owners: getSelectFilterValue(filters[ownerFilter.id]),
					focusAreaTypeIds: getSelectFilterValue(filters[focusAreaTypeFilter.id]),
					ancestorIds: getSelectFilterValue(filters[focusAreaUnderFilter.id]),
				},
				...(fg('talent_global_search')
					? {
							talentFilters: {
								owners: getSelectFilterValue(filters[ownerFilter.id]),
								employmentTypes: getSelectFilterValue(filters[positionEmploymentTypeFilter.id]).map(
									(value) => decodeURIComponent(value),
								),
								focusAreas: getSelectFilterValue(filters[positionFocusAreaFilter.id]),
								jobFamilies: getSelectFilterValue(filters[positionJobFamilyFilter.id]).map(
									(value) => decodeURIComponent(value),
								),
								statuses: getSelectFilterValue(filters[positionStatusFilter.id]).map((value) =>
									decodeURIComponent(value),
								),
								teams: getSelectFilterValue(filters[positionTeamFilter.id]).map((value) =>
									decodeURIComponent(value),
								),
							},
						}
					: {}),
				compassFilters: {
					componentFilter: {
						componentStates: getArchivedAndPendingComponentsValue(
							filters[archivedAndPendingComponentsFilter.id],
						),
					},
				},

				thirdPartyFilters: getThirdPartyFilters(
					filteredProducts,
					titleMatchValue,
					selectedEntities as TypeFilterValueKey[],
					thirdPartyConfigs,
					filterDateRange,
					assignee3PValues,
					containerAris,
					createdByValues,
					lastUpdatedByValues,
					ancestorAris,
					labelsValues,
					undefined,
					selectedDatasource?.datasourceId,
				),
			}) satisfies SelectedFiltersType,
		[
			ancestorAris,
			assignee3PValues,
			assigneeARIValues,
			commonContributors,
			containerAris,
			createdByValues,
			experience,
			filterDateRange,
			filteredProducts,
			filters,
			jiraIssueTypeFilters,
			jsmIncidentFilterIds,
			labelsValues,
			lastUpdatedByValues,
			pageStatusValue,
			reporterARIValues,
			selectedDatasource?.datasourceId,
			selectedDateRange,
			selectedEntities,
			selectedProduct,
			selectedTypes,
			thirdPartyConfigs,
			titleMatchValue,
			watcherARIValues,
			commenterARIValues,
		],
	);

	const unselectedProductsThirdPartyFilters = useMemo(
		() =>
			getThirdPartyFilters(
				availableProducts,
				titleMatchValue,
				selectedEntities as TypeFilterValueKey[],
				thirdPartyConfigs,
				filterDateRange,
			),

		[availableProducts, titleMatchValue, selectedEntities, thirdPartyConfigs, filterDateRange],
	);

	const {
		commonFilters,
		jiraFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		talentFilters,
	} = selectedFilters;

	const isEmpty =
		!commonFilters.range.lastModified &&
		!commonFilters.contributorsFilter &&
		selectedEntities.length === 0 &&
		confluenceFilters.spacesFilter.length === 0 &&
		!confluenceFilters.isVerified &&
		!confluenceFilters.titleMatchOnly &&
		confluenceFilters.contentStatuses.length === 0 &&
		confluenceFilters.containerStatus.length === 0 &&
		confluenceFilters.contributorsFilter.length === 0 &&
		confluenceFilters.ancestorIdsFilter.length === 0 &&
		confluenceFilters.range.length === 0 &&
		confluenceFilters.owners.length === 0 &&
		confluenceFilters.labelsFilter.length === 0 &&
		(!confluenceFilters.pageStatus || confluenceFilters.pageStatus.length === 0) &&
		jiraFilters.issueFilter.assigneeARIs.length === 0 &&
		(!jiraFilters.issueFilter['reporterARIs'] ||
			jiraFilters.issueFilter.reporterARIs.length === 0) &&
		jiraFilters.issueFilter.projectARIs.length === 0 &&
		(!jiraFilters.issueFilter.statusCategories ||
			jiraFilters.issueFilter.statusCategories?.length === 0) &&
		mercuryFilters.owners.length === 0 &&
		// When cleaning up rovo_search_in_jira_t25_fps_filters change this to jiraFilters.issueFilter.issueLabels.length === 0
		(!jiraFilters.issueFilter.issueLabels || jiraFilters.issueFilter.issueLabels?.length === 0) &&
		// When cleaning up ADD FG HERE change this to jiraFilters.issueFilter.issueLabels.length === 0
		(!jiraFilters.issueFilter.issueTypes || jiraFilters.issueFilter.issueTypes?.length === 0) &&
		mercuryFilters.owners.length === 0 &&
		mercuryFilters.focusAreaTypeIds.length === 0 &&
		mercuryFilters.ancestorIds.length === 0 &&
		(fg('talent_global_search')
			? talentFilters?.owners.length === 0 &&
				talentFilters?.employmentTypes.length === 0 &&
				talentFilters.focusAreas.length === 0 &&
				talentFilters?.jobFamilies.length === 0 &&
				talentFilters?.statuses.length === 0 &&
				talentFilters?.teams.length === 0
			: true) &&
		compassFilters.componentFilter.componentStates.length === 0 &&
		(fg('assets_rovo_search_all')
			? assetsFilters.objectSchemaFilter.schemaARIs.length === 0 &&
				assetsFilters.objectTypeFilter.typeARIs.length === 0
			: true) &&
		(!thirdPartyFilters.range || thirdPartyFilters?.range?.length === 0) &&
		!thirdPartyFilters.titleMatchOnly &&
		thirdPartyFilters.subtypes.length === 0 &&
		(!thirdPartyFilters.assignees || thirdPartyFilters.assignees?.length === 0) &&
		(!thirdPartyFilters.lastUpdatedBy || thirdPartyFilters.lastUpdatedBy?.length === 0) &&
		(!thirdPartyFilters.createdBy || thirdPartyFilters.createdBy?.length === 0) &&
		(!thirdPartyFilters.ancestorAris || thirdPartyFilters.ancestorAris?.length === 0) &&
		(!thirdPartyFilters.labels || thirdPartyFilters.labels?.length === 0);
	return useMemo(() => {
		return {
			selectedFilters,
			unselectedProductsThirdPartyFilters,
			selectedEntities,
			entitiesForResultsCountQuery,
			entities: entitiesForQuery,
			hasFiltersSelected: !isEmpty,
		};
	}, [
		entitiesForQuery,
		entitiesForResultsCountQuery,
		isEmpty,
		selectedEntities,
		selectedFilters,
		unselectedProductsThirdPartyFilters,
	]);
};

/**
 * QS-6991
 *
 * This hook is a shim allowing easy fg('search-page-relay-useselectedfilters-step-1')
 * This is a migration step in moving useSelectedFilters (a hook) to be getSelectedFilters (not a hook!)
 * This unblocks the relay migration and will allow an imperative search query approach
 *
 * In a later PR, we'll begin to pass in all the props needed at the callsites that have useSelectedFilters() and then even remove this hook!
 */
export const useGetSelectedFilters = ({
	experience = SearchExperience.SEARCH_PAGE,
}: {
	experience?: SearchStoreExperience;
}): SelectedFiltersReturnType => {
	const [search] = useSearch();
	const [selectedDatasource] = useSelectedDatasource();
	const [bootstrap] = useBootstrap();
	const currentUserId = useCurrentUserId();

	const { filters, selectedProducts, jsmIncidentFilterIds } = search;
	const { products, allFilterExcludedProducts, thirdPartyConfigs } = bootstrap;

	return useMemo(() => {
		return getSelectedFilters({
			filters,
			selectedProducts,
			selectedDatasource,
			availableProducts: products,
			allFilterExcludedProducts,
			thirdPartyConfigs,
			jsmIncidentFilterIds,
			experience,
			currentUserId,
		});
	}, [
		filters,
		selectedProducts,
		selectedDatasource,
		products,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		jsmIncidentFilterIds,
		experience,
		currentUserId,
	]);
};

/**
 * QS-6991 FG and hooks do not play well. See the ticket and go/how-to-fg-that for the approach being used
 */
export const useSelectedFilters = ({
	cloudId,
	thirdPartyConfigs,
	experience = SearchExperience.SEARCH_PAGE,
}: {
	cloudId: string | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	experience?: SearchStoreExperience;
}): SelectedFiltersReturnType => {
	return fg('search-page-relay-useselectedfilters-step-1')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useGetSelectedFilters({ experience })
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useWrappedSelectedFilters({
				thirdPartyConfigs,
				cloudId,
				experience,
			});
};
